import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Info, Coins, MessageCircle, Radio, Mic2, FileText, Store, Briefcase,
  ArrowLeftRight, Target, MapPin, Award, Shield, Zap, Heart, Users,
  HelpCircle, ExternalLink, Droplets, Code, ChevronDown, ChevronUp, GitBranch, BookOpen, Copy, Check, BadgeCheck, Rocket
} from 'lucide-react';
import { BCHLogo } from '../components/Layout';
import { useAppContext } from '../hooks/useAppContext';

const FEATURES = [
  {
    icon: <MessageCircle className="w-5 h-5" />,
    title: 'Social Feed',
    desc: 'Post, reply, repost, and react. Your social graph lives on Nostr relays, not a corporate server.',
    link: '/',
  },
  {
    icon: <Coins className="w-5 h-5" />,
    title: 'Instant BCH Tips',
    desc: 'Tip anyone from $0.01 with one click. Real Bitcoin Cash, sent on-chain in seconds.',
    link: '/',
  },
  {
    icon: <FileText className="w-5 h-5" />,
    title: 'BCH Ink',
    desc: 'Long-form writing from the BCH community. Publish with a clean visual editor and earn BCH tips on every article.',
    link: '/ink',
  },
  {
    icon: <Radio className="w-5 h-5" />,
    title: 'BCH Live',
    desc: 'Watch live streams from the BCH community, powered by Owncast. Verified users can go live.',
    link: '/live',
  },
  {
    icon: <Mic2 className="w-5 h-5" />,
    title: 'BCH Rooms',
    desc: 'Peer-to-peer live audio rooms — no server hears your voice, with BCH tips right over the conversation.',
    link: '/rooms',
  },
  {
    icon: <ArrowLeftRight className="w-5 h-5" />,
    title: 'P2P Exchange',
    desc: 'Trade BCH for local currency directly with other users. No middleman.',
    link: '/exchange',
  },
  {
    icon: <Target className="w-5 h-5" />,
    title: 'Crowdfunding',
    desc: 'Create and fund community projects with BCH. Transparent on-chain contributions.',
    link: '/crowdfund',
  },
  {
    icon: <Droplets className="w-5 h-5" />,
    title: 'Free BCH Faucet',
    desc: 'New to BCH? Get a small amount for free to start tipping and exploring.',
    link: '/faucet',
  },
  {
    icon: <Store className="w-5 h-5" />,
    title: 'Merchant Directory',
    desc: 'Find businesses that accept Bitcoin Cash near you or online.',
    link: '/merchants',
  },
  {
    icon: <BadgeCheck className="w-5 h-5" />,
    title: 'BCH Verified',
    desc: 'Community-verified reports from users visiting real BCH-accepting businesses.',
    link: '/verified',
  },
  {
    icon: <Briefcase className="w-5 h-5" />,
    title: 'Job Board',
    desc: 'Post and find jobs that pay in BCH. Work for sound money.',
    link: '/jobs',
  },
  {
    icon: <MapPin className="w-5 h-5" />,
    title: 'Community Map',
    desc: 'See BCH community members around the world on a live map.',
    link: '/map',
  },
  {
    icon: <Award className="w-5 h-5" />,
    title: 'Leaderboard',
    desc: 'See who\'s most active in the BCH community. Earn recognition for contributing.',
    link: '/leaderboard',
  },
];

export default function AboutPage() {
  const { user, login } = useAppContext();

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Info className="w-5 h-5 text-primary" /> About BCHNostr
        </h1>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-8">
        {/* Hero */}
        <div className="text-center py-6">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <BCHLogo size={64} />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-background rounded-full flex items-center justify-center border-2 border-primary">
                <Zap className="w-3 h-3 text-primary" />
              </div>
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-bch-gradient mb-2">BCHNostr</h2>
          <p className="text-lg text-muted-foreground font-medium">Freedom to Connect. Power to Reward.</p>
          <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto leading-relaxed">
            A decentralized social network where you own your identity, your content, and your money.
            Built on Nostr and Bitcoin Cash.
          </p>
        </div>

        {/* What makes it different */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
            <Heart className="w-4 h-4" /> Why BCHNostr?
          </h3>
          <div className="grid gap-3">
            {[
              {
                icon: <Shield className="w-5 h-5 text-primary" />,
                title: 'No middleman',
                desc: 'Your account is a cryptographic key. No email, no phone number, no company can ban you or delete your posts.',
              },
              {
                icon: <Coins className="w-5 h-5 text-primary" />,
                title: 'Real money, not points',
                desc: 'Tips are actual Bitcoin Cash sent on-chain. Writers, creators, and commenters earn real money for good content.',
              },
              {
                icon: <Users className="w-5 h-5 text-primary" />,
                title: 'Your data, everywhere',
                desc: 'Your posts live on Nostr relays across the internet. Switch apps anytime — your followers and content come with you.',
              },
            ].map(item => (
              <div key={item.title} className="glass-card flex items-start gap-3">
                <div className="mt-0.5 shrink-0">{item.icon}</div>
                <div>
                  <p className="text-sm font-semibold">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features grid */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
            <Zap className="w-4 h-4" /> Everything in one place
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {FEATURES.map(f => (
              <Link
                key={f.title}
                to={f.link}
                className="glass-card !p-3 hover:border-primary/30 transition-colors group"
              >
                <div className="text-primary mb-2 group-hover:scale-110 transition-transform">{f.icon}</div>
                <p className="text-xs font-semibold mb-0.5">{f.title}</p>
                <p className="text-[10px] text-muted-foreground leading-relaxed">{f.desc}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
            <Info className="w-4 h-4" /> How it works
          </h3>
          <div className="space-y-3">
            {[
              { step: '1', title: 'Create your identity', desc: 'Sign up with a Nostr browser extension (like nos2x or Alby) or generate a key pair. No personal info needed.' },
              { step: '2', title: 'Fund your wallet', desc: 'A BCH wallet is created automatically. Deposit any amount to start tipping, or grab some free BCH from the faucet.' },
              { step: '3', title: 'Connect and earn', desc: 'Post, write articles, join spaces, tip others, and earn BCH from the community. Your identity and funds are yours forever.' },
            ].map(item => (
              <div key={item.step} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
                  {item.step}
                </div>
                <div>
                  <p className="text-sm font-semibold">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Supported NIPs */}
        <SupportedNIPs />

        {/* Source Code */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
            <Code className="w-4 h-4" /> Source Code
          </h3>
          <div className="glass-card space-y-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              BCHNostr is open source. The entire codebase is published to Nostr via{' '}
              <a href="https://ngit.dev" target="_blank" rel="noopener" className="text-primary hover:underline">ngit</a>{' '}
              and can be browsed, cloned, forked, or self-hosted by anyone.
            </p>
            <a
              href="https://gitworkshop.dev/npub1madu4w57wnxpwexfwuawzcpfnh094nmeg9hze9n43kazyhn8qlxq4lrgfg/bchstr24"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-3 rounded-lg border border-border bg-muted/50 hover:border-primary/30 hover:shadow-md transition-all group"
            >
              <GitBranch className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold group-hover:text-primary transition-colors">Browse on GitWorkshop</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">View source code, commits, and issues</p>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0 group-hover:text-primary transition-colors" />
            </a>
            <CopyableCloneUrl />
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Built with React, Tailwind CSS, nostr-tools, and mainnet-js. No backend server required — the entire app runs in your browser and connects directly to Nostr relays and the BCH blockchain.
            </p>
          </div>
        </div>

        {/* CTA */}
        {/* Build your own — BCH Launch */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
            <Rocket className="w-4 h-4" /> Build your own
          </h3>
          <a
            href="https://bchlaunch.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-3 rounded-lg border border-border bg-muted/50 hover:border-primary/30 hover:shadow-md transition-all group"
          >
            <Rocket className="w-5 h-5 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold group-hover:text-primary transition-colors">Need a website? Launch one on BCH Launch</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Spin up your own Bitcoin Cash site or project at bchlaunch.com</p>
            </div>
            <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0 group-hover:text-primary transition-colors" />
          </a>
        </div>

        {!user && (
          <div className="glass-card text-center py-6">
            <BCHLogo size={40} />
            <h3 className="text-lg font-bold mt-3 mb-1">Ready to join?</h3>
            <p className="text-xs text-muted-foreground mb-4">No email. No phone number. Just you and your keys.</p>
            <button
              onClick={login}
              className="px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              Get Started
            </button>
          </div>
        )}

        {/* Links */}
        <div className="flex flex-wrap gap-3 justify-center text-xs">
          <Link to="/faq" className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors">
            <HelpCircle className="w-3.5 h-3.5" /> FAQ
          </Link>
          <a
            href="bitcoincash:qzfczm3kc9j8aw0pjm5azym8s8xg07ssru5yr5qepj"
            className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
          >
            <Coins className="w-3.5 h-3.5" /> Donate BCH
          </a>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener"
            className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Vibed with Shakespeare
          </a>
        </div>
      </div>
    </div>
  );
}

// ── Supported NIPs Section ─────────────────────────────────────────
// Lists every NIP implemented with event kinds and what they're used for.

const SUPPORTED_NIPS: {
  nip: string;
  name: string;
  kinds?: string[];
  usage: string;
}[] = [
  {
    nip: 'NIP-01',
    name: 'Basic Protocol',
    kinds: ['0', '1', '3', '5', '6', '7'],
    usage: 'Profiles (kind 0), short text notes (kind 1), contact lists / follows (kind 3), event deletion (kind 5), reposts (kind 6), reactions / likes (kind 7).',
  },
  {
    nip: 'NIP-04',
    name: 'Encrypted Direct Messages',
    kinds: ['4'],
    usage: 'End-to-end encrypted private messages between users.',
  },
  {
    nip: 'NIP-19',
    name: 'bech32-Encoded Entities',
    usage: 'Human-readable encoding for keys and events: npub, nsec, note, naddr. Used throughout the app for sharing profiles, posts, and article links.',
  },
  {
    nip: 'NIP-23',
    name: 'Long-Form Content',
    kinds: ['30023'],
    usage: 'Articles published and read through BCH Ink (the long-form section). Supports title, summary, cover image, and hashtags.',
  },
  {
    nip: 'NIP-25',
    name: 'Reactions',
    kinds: ['7'],
    usage: 'Like / react to posts and articles. Displayed as heart counts on posts.',
  },
  {
    nip: 'NIP-44',
    name: 'Versioned Encryption',
    usage: 'Encrypts and decrypts the BCH wallet private key (WIF) for secure backup to Nostr relays. Also used for faucet claim storage.',
  },
  {
    nip: 'NIP-53',
    name: 'Live Activities',
    kinds: ['30311', '1311'],
    usage: 'Livestreaming: stream status and metadata (kind 30311), live chat messages (kind 1311).',
  },
  {
    nip: 'NIP-57',
    name: 'Zaps / Lightning Zaps',
    kinds: ['9735'],
    usage: 'Tip receipts. When a BCH tip is sent, a kind 9735 event records the amount, recipient, and transaction for social proof and leaderboard tracking.',
  },
  {
    nip: 'NIP-78',
    name: 'Application-Specific Data',
    kinds: ['30078'],
    usage: 'Stores user\'s BCH receiving address and encrypted wallet backup. Addressable events keyed by d-tag so they can be updated.',
  },
  {
    nip: 'NIP-88',
    name: 'Polls',
    kinds: ['1068', '1018'],
    usage: 'Community polls: create single-choice polls (kind 1068) and cast votes (kind 1018). Results update in real-time.',
  },
  {
    nip: 'NIP-96',
    name: 'HTTP File Storage Integration',
    usage: 'Authenticated image and video uploads to nostr.build, satellite.earth, and other NIP-96-compatible media hosts.',
  },
  {
    nip: 'NIP-98',
    name: 'HTTP Auth',
    kinds: ['27235'],
    usage: 'Signs HTTP requests for authenticated media uploads. Used by NIP-96 and Blossom upload services.',
  },
  {
    nip: 'Blossom (BUD-02)',
    name: 'Blob Upload/Download',
    kinds: ['24242'],
    usage: 'Authenticated media uploads to Blossom servers (e.g., blossom.primal.net). Uses SHA-256 file hash for deduplication.',
  },
];

function SupportedNIPs() {
  const [expanded, setExpanded] = useState(false);
  const visibleNips = expanded ? SUPPORTED_NIPS : SUPPORTED_NIPS.slice(0, 5);

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
        <BookOpen className="w-4 h-4" /> Supported NIPs
      </h3>
      <p className="text-xs text-muted-foreground leading-relaxed">
        BCHNostr implements {SUPPORTED_NIPS.length} Nostr Implementation Possibilities (NIPs) covering social networking, encryption, media, livestreaming, and more.
      </p>
      <div className="space-y-1.5">
        {visibleNips.map(nip => (
          <NipRow key={nip.nip} nip={nip} />
        ))}
      </div>
      {SUPPORTED_NIPS.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs text-primary hover:underline mx-auto"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3.5 h-3.5" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3.5 h-3.5" />
              Show all {SUPPORTED_NIPS.length} NIPs
            </>
          )}
        </button>
      )}
    </div>
  );
}

function NipRow({ nip }: { nip: typeof SUPPORTED_NIPS[0] }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="glass-card !p-0 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/30 transition-colors"
      >
        <span className="text-[10px] font-mono font-bold text-primary bg-primary/10 px-2 py-0.5 rounded shrink-0">
          {nip.nip}
        </span>
        <span className="text-xs font-medium flex-1 min-w-0 truncate">{nip.name}</span>
        {nip.kinds && (
          <span className="text-[9px] text-muted-foreground font-mono shrink-0 hidden sm:block">
            {nip.kinds.length <= 3 ? nip.kinds.map(k => `k:${k}`).join(' ') : `${nip.kinds.length} kinds`}
          </span>
        )}
        {open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className="px-3 pb-2.5 border-t border-border pt-2">
          <p className="text-[11px] text-muted-foreground leading-relaxed">{nip.usage}</p>
          {nip.kinds && (
            <div className="flex flex-wrap gap-1 mt-2">
              {nip.kinds.map(k => (
                <span key={k} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  kind {k}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Copyable Clone URL ──────────────────────────────────────────────

const CLONE_URL = 'nostr://npub1madu4w57wnxpwexfwuawzcpfnh094nmeg9hze9n43kazyhn8qlxq4lrgfg/relay.ngit.dev/bchstr24';

function CopyableCloneUrl() {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(`git clone ${CLONE_URL}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-2">
      {/* Clone command */}
      <div
        onClick={handleCopy}
        className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-border bg-muted/50 hover:border-primary/30 cursor-pointer transition-colors group"
      >
        <Code className="w-4 h-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0 overflow-x-auto">
          <p className="text-[10px] text-muted-foreground mb-0.5">Clone with ngit:</p>
          <p className="text-[11px] font-mono text-foreground whitespace-nowrap">
            git clone nostr://npub1madu4w...qlxq4lrgfg/relay.ngit.dev/bchstr24
          </p>
        </div>
        {copied ? (
          <span className="text-[10px] text-emerald-500 font-medium shrink-0 flex items-center gap-1">
            <Check className="w-3 h-3" /> Copied!
          </span>
        ) : (
          <Copy className="w-3.5 h-3.5 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </div>
      <p className="text-[10px] text-muted-foreground leading-relaxed">
        Install <a href="https://ngit.dev" target="_blank" rel="noopener" className="text-primary hover:underline">ngit</a> to clone.
        The source code lives on Nostr relays — no GitHub account needed.
      </p>
    </div>
  );
}

